import React, { useCallback, useEffect } from 'react';
import { DocumentTitle } from '@atlassian/jira-global-document-title';
import { useIntl } from '@atlassian/jira-intl';
import { Spinner } from '@atlassian/jira-portfolio-3-common';
import FlagGroup from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/flag-group';
import {
	DEPENDENCY_REPORT_SHARE_TYPE,
	TIMELINE_SHARE_TYPE,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import ProjectOverLimitErrorPage from '@atlassian/jira-portfolio-3-portfolio/src/common/view/project-exceed-limit-error-page';
import { getPlanUrl } from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import DependencyReportMacroView from './dependencies-report';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import TimelineMacroView from './timeline';
import type { Props } from './types';

const ConfluenceMacroView = ({
	initialLoad,
	setInitialFilterValues,
	confluenceMacroType,
	isLoading,
	isProjectLimitExceeded,
	locale,
	isEmbed,
	planInfo,
}: Props) => {
	const { formatMessage } = useIntl();

	useEffect(() => {
		initialLoad({ locale });
		setInitialFilterValues();

		// Scope this global style to the ARJ package to prevent it leaking into other packages' storybooks
		window.document.body.style.overflowY = 'hidden';

		return () => {
			// Remove the inline styles
			if (window.document.body.style.overflowY === 'hidden') {
				window.document.body.style.overflowY = '';
			}
		};
	}, [initialLoad, locale, setInitialFilterValues]);

	const renderView = useCallback(() => {
		if (isProjectLimitExceeded) {
			return (
				<ProjectOverLimitErrorPage
					isEmbedded={isEmbed}
					planUrl={
						planInfo.id != null
							? getPlanUrl({
									planId: planInfo.id,
									scenarioId: planInfo.currentScenarioId,
									confluenceMacroType,
								})
							: undefined
					}
				/>
			);
		}

		if (isLoading)
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<div className={styles.wrapperLoading}>
					<Spinner size="large" />
				</div>
			);

		switch (confluenceMacroType) {
			case DEPENDENCY_REPORT_SHARE_TYPE:
				return <DependencyReportMacroView />;
			case TIMELINE_SHARE_TYPE:
			default:
				return <TimelineMacroView />;
		}
	}, [
		confluenceMacroType,
		isLoading,
		isEmbed,
		isProjectLimitExceeded,
		planInfo.id,
		planInfo.currentScenarioId,
	]);

	const productName = formatMessage(messages.plans);
	const pageTitle =
		planInfo?.title != null ? `${planInfo.title} - ${productName}` : `${productName}`;
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={styles.app} data-name="ManualPlans">
			<DocumentTitle title={pageTitle} />
			{renderView()}
			<FlagGroup />
		</div>
	);
};

export default ConfluenceMacroView;
